import React from 'react';
import { cn } from '../../lib/utils';
import { getInitials } from '../../lib/utils';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  name: string;
  src?: string;
  size?: AvatarSize;
  online?: boolean;
  className?: string;
}

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-7 w-7 text-xs',
  md: 'h-8 w-8 text-sm',
  lg: 'h-10 w-10 text-sm',
  xl: 'h-12 w-12 text-base',
};

const dotSizeClasses: Record<AvatarSize, string> = {
  xs: 'h-1.5 w-1.5 border',
  sm: 'h-2 w-2 border',
  md: 'h-2.5 w-2.5 border',
  lg: 'h-3 w-3 border-[1.5px]',
  xl: 'h-3.5 w-3.5 border-2',
};

const colorMap: Record<string, string> = {
  A: 'bg-red-100 text-red-700',
  B: 'bg-orange-100 text-orange-700',
  C: 'bg-amber-100 text-amber-700 dark:text-amber-300',
  D: 'bg-yellow-100 text-yellow-700',
  E: 'bg-lime-100 text-lime-700',
  F: 'bg-green-100 text-green-700',
  G: 'bg-emerald-100 text-emerald-700',
  H: 'bg-teal-100 text-teal-700',
  I: 'bg-cyan-100 text-cyan-700',
  J: 'bg-sky-100 text-sky-700',
  K: 'bg-blue-100 text-blue-700 dark:text-blue-300',
  L: 'bg-indigo-100 text-indigo-700 dark:text-indigo-300',
  M: 'bg-violet-100 text-violet-700',
  N: 'bg-purple-100 text-purple-700',
  O: 'bg-fuchsia-100 text-fuchsia-700',
  P: 'bg-pink-100 text-pink-700',
  Q: 'bg-rose-100 text-rose-700',
  R: 'bg-red-100 text-red-700',
  S: 'bg-orange-100 text-orange-700',
  T: 'bg-teal-100 text-teal-700',
  U: 'bg-indigo-100 text-indigo-700 dark:text-indigo-300',
  V: 'bg-violet-100 text-violet-700',
  W: 'bg-blue-100 text-blue-700 dark:text-blue-300',
  X: 'bg-sky-100 text-sky-700',
  Y: 'bg-yellow-100 text-yellow-700',
  Z: 'bg-zinc-100 text-zinc-700',
};

function getAvatarColor(name: string): string {
  const first = (name ?? '')?.[0]?.toUpperCase() || 'A';
  return colorMap[first] || 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300';
}

export function Avatar({ name = '', src, size = 'md', online, className }: AvatarProps) {
  return (
    <div className={cn('relative inline-flex shrink-0', className)}>
      <div
        className={cn(
          'rounded-full flex items-center justify-center font-semibold overflow-hidden',
          sizeClasses[size],
          !src && getAvatarColor(name)
        )}
      >
        {src ? (
          <img src={src} alt={name} className="h-full w-full object-cover" />
        ) : (
          <span>{getInitials(name)}</span>
        )}
      </div>
      {online !== undefined && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-white dark:border-slate-800',
            dotSizeClasses[size],
            online ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-slate-500'
          )}
        />
      )}
    </div>
  );
}
